import { useEffect, useMemo, useState } from "react";
import type { Locale } from "@vzlive/shared";
import { t } from "@vzlive/shared/i18n";

const remaining = (target: string) =>
  Math.max(0, new Date(target).getTime() - Date.now());

export function Countdown({
  target,
  locale,
}: {
  target: string;
  locale: Locale;
}) {
  const [milliseconds, setMilliseconds] = useState(() => remaining(target));

  useEffect(() => {
    const timer = window.setInterval(
      () => setMilliseconds(remaining(target)),
      1_000,
    );
    return () => window.clearInterval(timer);
  }, [target]);

  const values = useMemo(() => {
    const totalSeconds = Math.floor(milliseconds / 1_000);
    return {
      days: Math.floor(totalSeconds / 86_400),
      hours: Math.floor((totalSeconds % 86_400) / 3_600),
      minutes: Math.floor((totalSeconds % 3_600) / 60),
      seconds: totalSeconds % 60,
    };
  }, [milliseconds]);

  return (
    <section
      className="countdown"
      aria-label={t(locale, "countdown")}
      aria-live="off"
    >
      <p>{t(locale, "countdown")}</p>
      <div>
        {(
          [
            ["days", t(locale, "days")],
            ["hours", t(locale, "hours")],
            ["minutes", t(locale, "minutes")],
            ["seconds", t(locale, "seconds")],
          ] as const
        ).map(([key, label]) => (
          <span key={key}>
            <strong>{String(values[key]).padStart(2, "0")}</strong>
            <small>{label}</small>
          </span>
        ))}
      </div>
    </section>
  );
}
